import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';

export interface KYCDocument {
  CreationDate: number;
  Flags: number[];
  Id: string;
  ProcessedDate: number | null;
  RefusedReasonMessage: string | null;
  RefusedReasonType:
    | 'DOCUMENT_DO_NOT_MATCH_USER_DATA'
    | 'DOCUMENT_FALSIFIED'
    | 'DOCUMENT_HAS_EXPIRED'
    | 'DOCUMENT_INCOMPLETE'
    | 'DOCUMENT_MISSING'
    | 'DOCUMENT_NOT_ACCEPTED'
    | 'DOCUMENT_UNREADABLE'
    | 'SPECIFIC_CASE'
    | 'UNDERAGE_PERSON'
    | null;
  Status:
    | 'CREATED'
    | 'VALIDATION_ASKED'
    | 'VALIDATED'
    | 'REFUSED'
    | 'OUT_OF_DATE';
  Tag: string | null;
  Type:
    | 'IDENTITY_PROOF'
    | 'REGISTRATION_PROOF'
    | 'ARTICLES_OF_ASSOCIATION'
    | 'SHAREHOLDER_DECLARATION'
    | 'ADDRESS_PROOF';
  UserId: string;
}

export interface GetKycDocumentParams {
  params: {
    kycDocumentId: string;
  };
}

export interface CreateKycDocumentParams {
  body: {
    Type:
      | 'IDENTITY_PROOF'
      | 'REGISTRATION_PROOF'
      | 'ARTICLES_OF_ASSOCIATION'
      | 'SHAREHOLDER_DECLARATION'
      | 'ADDRESS_PROOF';
    pages: string[];
  };
}

export interface KycUser {
  Id: string;
  KYCLevel: 'LIGHT' | 'REGULAR';
  LegalRepresentativeProofOfIdentity: string | null;
  ProofOfRegistration: string | null;
  TermsAndConditionsAccepted: boolean;
  TermsAndConditionsAcceptedDate: number;
  UserCategory: 'PAYER' | 'OWNER';
}

export interface GetKycDocumentsResult {
  user: KycUser;
  kyc_documents: KYCDocument[];
}

export interface UBODeclaration {
  Id: string;
  Status:
    | 'CREATED'
    | 'VALIDATION_ASKED'
    | 'INCOMPLETE'
    | 'VALIDATED'
    | 'REFUSED';
  CreationDate: number;
  ProcessedDate: number | null;
  Reason: string | null;
  Message: string | null;
  Ubos: UBO[];
}

export interface UBO {
  Id: string;
  FirstName: string;
  LastName: string;
  Birthday: number; // Unix timestamp
  Nationality: string;
  Address: {
    AddressLine1: string;
    City: string;
    PostalCode: string;
    Country: string;
  };
  Birthplace: {
    City: string;
    Country: string;
  };
}

export interface CreateUBOParams {
  ubo: {
    FirstName: string;
    LastName: string;
    Birthday: Date;
    Nationality: string;
    Address: {
      AddressLine1: string;
      City: string;
      PostalCode: string;
      Country: string;
    };
    Birthplace: {
      City: string;
      Country: string;
    };
  };
}

export interface IKycPhoenixApi extends Api {
  getKycDocuments(): Promise<GetKycDocumentsResult>;
  getKycDocument(params: GetKycDocumentParams): Promise<KYCDocument>;
  createKycDocument(params: CreateKycDocumentParams): Promise<KYCDocument>;
  createUBODeclaration(params: Record<never, never>): Promise<UBODeclaration>;
  listUBODeclarations(): Promise<{ ubo_declarations: UBODeclaration[] }>;
  getUBODeclaration(params: {
    params: {
      ubo_declaration_id: string;
    };
  }): Promise<UBODeclaration>;
  createUBO(
    params: { params: { ubo_declaration_id: string } } & {
      body: CreateUBOParams;
    }
  ): Promise<UBO>;
  submitUBODeclaration(params: {
    params: {
      ubo_declaration_id: string;
    };
  }): Promise<UBODeclaration>;
  deleteUBO(params: {
    params: {
      ubo_declaration_id: string;
      ubo_id: string;
    };
  }): Promise<void>;
}

export class KycPhoenixApi implements IKycPhoenixApi {
  baseUrl: string;
  headers: Headers;

  getKycDocuments: IKycPhoenixApi['getKycDocuments'];
  getKycDocument: IKycPhoenixApi['getKycDocument'];
  createKycDocument: IKycPhoenixApi['createKycDocument'];
  createUBODeclaration: IKycPhoenixApi['createUBODeclaration'];
  listUBODeclarations: IKycPhoenixApi['listUBODeclarations'];
  getUBODeclaration: IKycPhoenixApi['getUBODeclaration'];
  createUBO: IKycPhoenixApi['createUBO'];
  submitUBODeclaration: IKycPhoenixApi['submitUBODeclaration'];
  deleteUBO: IKycPhoenixApi['deleteUBO'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl('service-provider', apiUrl);
    this.headers = new Headers({
      'content-type': 'application/json',
      'x-pn-stripemode': 'live',
    });

    this.getKycDocuments = get({
      authContext,
      headers: this.headers,
      url: `${this.baseUrl}/kyc`,
    });

    this.getKycDocument = get({
      authContext,
      headers: this.headers,
      url: (params) => `${this.baseUrl}/kyc/${params.kycDocumentId}`,
    });

    const createHeaders = new Headers(this.headers);
    createHeaders.delete('content-type');
    // createHeaders.append("content-type", 'multipart')

    this.createKycDocument = modify({
      authContext,
      headers: createHeaders,
      url: `${this.baseUrl}/kyc`,
      transformBody: ({ Type, pages }) => {
        const formData = new FormData();

        formData.append('Type', Type);
        for (const page of pages) {
          formData.append('pages[]', page);
        }

        return formData;
      },
      method: 'POST',
    });

    this.createUBODeclaration = modify({
      authContext,
      headers: this.headers,
      url: `${this.baseUrl}/kyc/ubo`,
      method: 'POST',
    });

    this.listUBODeclarations = get({
      authContext,
      headers: this.headers,
      url: `${this.baseUrl}/kyc/ubo`,
    });

    this.getUBODeclaration = get({
      authContext,
      headers: this.headers,
      url: (params) => `${this.baseUrl}/kyc/ubo/${params.ubo_declaration_id}`,
    });

    this.createUBO = modify({
      authContext,
      headers: this.headers,
      url: (params) =>
        `${this.baseUrl}/kyc/ubo/${params.ubo_declaration_id}/ubos`,
      method: 'POST',
      transformBody: ({ ubo }) => ({
        ubo: {
          ...ubo,
          Birthday: Math.floor(ubo.Birthday.getTime() / 1000),
        },
      }),
    });

    this.submitUBODeclaration = modify({
      authContext,
      headers: this.headers,
      url: (params) =>
        `${this.baseUrl}/kyc/ubo/${params.ubo_declaration_id}/submit`,
      method: 'PUT',
    });

    this.deleteUBO = modify({
      authContext,
      headers: this.headers,
      url: (params) =>
        `${this.baseUrl}/kyc/ubo/${params.ubo_declaration_id}/ubos/${params.ubo_id}`,
      method: 'DELETE',
    });
  }
}
